/**
 * Shared money-input helpers — the single source of truth for every
 * money field in the SPA (fields carrying the ``data-money-input``
 * marker, rendered by ``DecimalTextInput`` server-side or hand-written
 * with ``type="text" inputmode="decimal"``).
 *
 * Display convention: locale comma ("120,50"). Wire/DB convention:
 * dot — the server normalises on POST (``DecimalTextInput
 * .value_from_datadict`` or ``apps.core.money.parse_money``), so these
 * helpers only ever deal with the DISPLAY side plus comma-aware
 * parsing for client-side arithmetic.
 *
 * Consumers: the global ``data-money-input`` sanitizer in app.js, and
 * the page modules that do client-side money math (job-form,
 * credit-notes, invoice-detail).
 */

/**
 * Parse a money string (locale comma OR dot) into a Number.
 * Returns NaN for null/empty/garbage — callers test Number.isNaN.
 */
export function parseMoney(value) {
    if (value == null) return NaN;
    return parseFloat(String(value).replace(',', '.').trim());
}

/** Format a Number as a 2-decimal DISPLAY string with the locale comma. */
export function formatMoney(num) {
    return num.toFixed(2).replace('.', ',');
}

/**
 * Round to cents the way the server's ``money_q`` does (half-up for
 * positive amounts) so client-side previews always match what the
 * server will store.
 */
export function roundCents(num) {
    return Math.round(num * 100) / 100;
}

/**
 * Normalize a money field's text in place for DISPLAY: accept a typed
 * comma OR dot, drop any other char, unify on a single comma, keep one
 * decimal separator. Caret is best-effort preserved so typing "120,5"
 * never clears the input or jumps the cursor.
 *
 * Negative amounts are opt-in per field via ``data-money-allow-negative``
 * (e.g. manual invoice lines used as discounts): a single LEADING minus
 * is preserved, anything else stays digits/comma. Fields without the
 * attribute keep the historical strictly-positive behaviour.
 */
export function sanitizeMoneyField(el) {
    const original = el.value;
    const allowNegative = !!(el.dataset && el.dataset.moneyAllowNegative !== undefined);
    const isNegative = allowNegative && original.trimStart().startsWith('-');
    let cleaned = original.replace(/[^0-9.,]/g, '').replace(/\./g, ',');
    const firstSep = cleaned.indexOf(',');
    if (firstSep !== -1) {
        cleaned = cleaned.slice(0, firstSep + 1)
            + cleaned.slice(firstSep + 1).replace(/,/g, '');
    }
    if (isNegative) cleaned = '-' + cleaned;
    if (cleaned === original) return;
    const caret = el.selectionStart;
    const delta = original.length - cleaned.length;
    el.value = cleaned;
    if (caret !== null && typeof el.setSelectionRange === 'function') {
        const pos = Math.max(0, caret - delta);
        try { el.setSelectionRange(pos, pos); } catch (_e) { /* noop */ }
    }
}
